import { HttpErrorResponse } from "@angular/common/http";
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnInit,
  Output,
  signal,
  ViewEncapsulation,
} from "@angular/core";
import {
  FormControl,
  FormGroup,
  NonNullableFormBuilder,
  Validators,
} from "@angular/forms";
import { AnalyticsService } from "@cariloop/analytics";
import { phonePattern } from "@cariloop/ui-components";
import { AccountService } from "app/shared/services/account.service";
import { BrazeService } from "app/shared/services/braze.service";
import {
  DevicePlatformService,
  MixPanelPlatform,
} from "app/shared/services/device-platform.service";
import type { RegisteredEmail } from "app/shared/types/register.types";
import { EMPTY } from "rxjs";
import { catchError } from "rxjs/operators";

type ErrorType = "email-already-used" | "generic-error" | "gateway-timeout";

type State = {
  hasServerSideError: boolean;
  errorType: ErrorType;
  isSubmitting: boolean;
};

const INITIAL_STATE: State = {
  hasServerSideError: false,
  errorType: undefined,
  isSubmitting: false,
};

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  selector: "app-existing-backup-contact",
  templateUrl: "./existing-backup-contact.component.html",
  styleUrls: ["./existing-backup-contact.component.scss"],
  standalone: false,
})
export class ExistingBackupContactComponent implements OnInit {
  @Output()
  nextStep = new EventEmitter<RegisteredEmail>();

  emailAddress = this.getAccountInfo().email;
  accountId = this.getAccountInfo().id;
  platform: MixPanelPlatform;

  state = signal<State>(INITIAL_STATE);

  existingBackupContactForm = this.formBuilder.group({
    alternateContactMethodGroup: this.formBuilder.group({
      phoneNumber: ["", [Validators.pattern(phonePattern)]],
    }),
    consentMarketing: [true],
  });

  constructor(
    private readonly formBuilder: NonNullableFormBuilder,
    private readonly analyticsService: AnalyticsService,
    private readonly accountService: AccountService,
    private readonly devicePlatformService: DevicePlatformService,
    private readonly brazeService: BrazeService,
  ) {}

  ngOnInit(): void {
    this.platform = this.devicePlatformService.getPlatformForMixpanel();

    this.analyticsService.trackEvent("back-up-contact", {
      platform: this.platform,
      source: "auth",
      accountId: this.accountId,
      userFlow: "update-existing-account",
    });
  }

  handleSubmit() {
    this.state.set(INITIAL_STATE);
    this.existingBackupContactForm.markAllAsTouched();

    if (!this.existingBackupContactForm.valid) {
      return;
    }

    this.state.update((currentState) => ({
      ...currentState,
      isSubmitting: true,
    }));
    const account = this.accountService.getLogged();
    if (account instanceof Error) {
      throw account;
    }

    const phone = this.existingBackupContactForm.get(
      "alternateContactMethodGroup.phoneNumber",
    ).value;

    const currentNotificationConfigs = account.notificationConfigs || {
      email: true,
      push: true,
    };
    const notificationConfigs = {
      ...currentNotificationConfigs,
      ...(this.existingBackupContactForm.get("consentMarketing").value
        ? { optInDate: new Date().toISOString() }
        : { optOutDate: new Date().toISOString() }),
    };

    if (this.existingBackupContactForm.get("consentMarketing").value) {
      this.brazeService.smsOptInForUser(account.id, phone);
    } else {
      this.brazeService.smsOptOutForUser(account.id, phone);
    }

    const data: {
      phone: string;
      notificationConfigs: { optInDate?: string; optOutDate?: string };
    } = {
      phone,
      notificationConfigs,
    };

    this.accountService
      .updateAttributes(account.id, data)
      .pipe(
        catchError((errorResponse: HttpErrorResponse) => {
          const error = {
            ...errorResponse.error.error,
          };
          let errorType: ErrorType = "generic-error";

          if (
            error.statusCode === 422 &&
            error.message === "Secondary email is already in use."
          ) {
            errorType = "email-already-used";
          } else if (errorResponse.status === 504) {
            errorType = "gateway-timeout";
          }

          this.state.update((currentState) => ({
            ...currentState,
            isSubmitting: false,
            hasServerSideError: true,
            errorType,
          }));
          return EMPTY;
        }),
      )
      .subscribe(() => {
        this.analyticsService.trackEvent("back-up-contact-complete", {
          platform: this.platform,
          source: "auth",
          accountId: this.accountId,
          userFlow: "update-existing-account",
        });

        this.analyticsService.trackEvent("Marketing_SMS_preference_added", {
          platform: this.platform,
          source: "auth",
          accountId: this.accountId,
          userFlow: "update-existing-account",
          status: this.existingBackupContactForm.get("consentMarketing").value
            ? "opt_in"
            : "opt_out",
        });

        this.state.update((currentState) => ({
          ...currentState,
          isSubmitting: false,
        }));
        this.nextStep.emit({
          type: "personal",
          address: this.emailAddress,
        });
      });
  }

  private getAccountInfo(): { email: string; id: string } {
    const accountResult = this.accountService.getLogged();
    if (accountResult instanceof Error) {
      throw accountResult;
    }

    return { email: accountResult.email, id: accountResult.id };
  }
}
